import { defineStore } from "pinia";
import type {
  Reward,
  RewardCategoryFilter,
  RewardBrandFilter,
  RewardDetailsResponse,
  RewardsFilterResponse,
  RewardsPayload,
  RewardTypeFilter,
} from "~/types/rewards";
import type { IModuleCountResponse } from "~/types/common";
const { convertFilterToBase64 } = useBase64();
export const useRewardsStore = defineStore("rewardsStore", {
  state: () => ({
    rewards: [] as Reward[],
    rewardDetails: null as RewardDetailsResponse | null,
    moduleCount: null as IModuleCountResponse | null,
    payload: {
      page: 1,
      per_page: 9,
      sort: "id",
      order_by: "ASC",
      search: "",
      filters: "",
    } as RewardsPayload,
    current_page: 1,
    last_page: 1,
    denomination: null as string | null,
    selectedFilters: {
      rewardTypes: [] as string[],
      categories: [] as string[],
      brands: [] as string[],
    },
    rewardTypeFilters: [] as RewardTypeFilter[],
    rewardCategoryFilters: [] as RewardCategoryFilter[],
    rewardBrandFilters: [] as RewardBrandFilter[],
    isLoading: false,
  }),
  actions: {
    setRewards(data: Reward[]) {
      this.rewards = data;
    },
    setRewardDetails(data: RewardDetailsResponse) {
      this.rewardDetails = data;
    },
    setModuleCount(data: IModuleCountResponse) {
      this.moduleCount = data;
    },
    setPagination(pagination: { currentPage: number; lastPage: number }) {
      this.current_page = pagination.currentPage;
      this.last_page = pagination.lastPage;
    },
    setDenomination(data: string) {
      this.denomination = data;
    },

    setFilter(data: RewardsFilterResponse) {
      this.rewardTypeFilters = data.reward_type_filters;
      this.rewardCategoryFilters = data.reward_category_filters;
      this.rewardBrandFilters = data.reward_brand_filters;
    },
    setSelectedFilters(filters: {
      rewardTypes: string[];
      categories: string[];
      brands: string[];
    }) {
      if (filters.rewardTypes)
        this.selectedFilters.rewardTypes = filters.rewardTypes;
      if (filters.categories)
        this.selectedFilters.categories = filters.categories;
      if (filters.brands) this.selectedFilters.brands = filters.brands;
    },
    setFilters(filters: {
      reward_type_id?: string;
      reward_category_id?: string;
      brand_name?: string;
    }) {
      const updatedFilters: {
        rewardTypes?: string[];
        categories?: string[];
        brands?: string[];
      } = {
        rewardTypes: [],
        categories: [],
        brands: [],
      };
      if (filters.reward_type_id) {
        updatedFilters.rewardTypes?.push(filters.reward_type_id);
        this.selectedFilters.rewardTypes.push(filters.reward_type_id);
      } else {
        delete updatedFilters.rewardTypes;
      }
      if (filters.reward_category_id) {
        updatedFilters.categories?.push(filters.reward_category_id);
        this.selectedFilters.categories.push(filters.reward_category_id);
      } else {
        delete updatedFilters.categories;
      }
      if (filters.brand_name) {
        updatedFilters.brands?.push(filters.brand_name);
        this.selectedFilters.brands.push(filters.brand_name);
      } else {
        delete updatedFilters.brands;
      }
      this.payload.filters = convertFilterToBase64(updatedFilters);
    },
    getRewards() {
      return this.rewards;
    },
    getrewardDetails() {
      return this.rewardDetails;
    },
    getDenomination() {
      return this.denomination;
    },
    getallmodulecount() {
      return this.moduleCount;
    },
    getcurrencyFactor() {
      return this.rewardDetails?.data?.currency_factor;
    },
    getminDenomination() {
      return this.rewardDetails?.data?.min_denomination;
    },
    getmaxDenomination() {
      return this.rewardDetails?.data?.max_denomination;
    },
    getAvailablePoints() {
      return this.moduleCount?.available_points;
    },
    getDescription() {
      return this.rewardDetails?.data?.description;
    },
    getTermsAndConditions() {
      return this.rewardDetails?.data?.terms_and_conditions;
    },
    getLogo() {
      return this.moduleCount?.logo;
    },
    clearRewards() {
      this.rewards = [];
      this.payload.page = 1;
      this.payload.per_page = 9;
      this.payload.sort = "id";
      this.payload.order_by = "ASC";
      this.payload.search = "";
      this.payload.filters = "";
    },
    clearFilters() {
      this.selectedFilters.rewardTypes = [];
      this.selectedFilters.categories = [];
      this.selectedFilters.brands = [];
      this.payload.filters = "";
    },
    setLoading(loading: boolean) {
      this.isLoading = loading;
    },
  },
  getters: {
    getIsLoading: (state) => state.isLoading,
  },
});
